import React, { useState } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Box, TextField, InputAdornment, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

const DataTableRCM = ({ columns, rows, columnsWithActions, height = 400, sx, ...props }) => {
    const [searchText, setSearchText] = useState('');
    const [filteredRows, setFilteredRows] = useState(rows);
    
    // Handle search input changes
    React.useEffect(() => {
        if (!searchText) {
            setFilteredRows(rows);
            return;
        }
        
        const searchLower = searchText.toLowerCase();
        const filtered = rows.filter(row => {
            return Object.keys(row).some(key => {
                const value = row[key];
                return value !== null && 
                       value !== undefined && 
                       value.toString().toLowerCase().includes(searchLower);
            });
        });
        
        setFilteredRows(filtered);
    }, [searchText, rows]);

    const getRowClassName = (params) => {
        return params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row';
    };

    const customTheme = createTheme({
        components: {
            MuiDataGrid: {
                styleOverrides: {
                    root: {
                        border: 'none',
                        maxHeight: '100%',
                        '& .MuiDataGrid-main': {
                            borderRadius: '8px',
                            overflow: 'hidden',
                            '&:focus-within': {
                                outline: 'none',
                            },
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#ffffff',
                            borderBottom: '1px solid rgba(224, 224, 224, 0.4)',
                            '& .MuiDataGrid-columnSeparator': {
                                display: 'none',
                            },
                        },
                        '& .MuiDataGrid-columnHeader': {
                            color: 'rgba(0, 0, 0, 0.6)',
                            fontWeight: '500',
                            fontSize: '0.8125rem',
                            padding: '0 16px',
                            height: '48px !important',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                            },
                            '&:focus': {
                                outline: 'none',
                            },
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 500,
                            letterSpacing: '0.01em',
                            textTransform: 'uppercase',
                            fontSize: '0.75rem',
                        },
                        '& .MuiDataGrid-columnHeaderTitleContainer': {
                            padding: '0 4px',
                        },
                        '& .MuiDataGrid-columnSeparator': {
                            display: 'none'
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid rgba(224, 224, 224, 0.3)',
                            color: '#333',
                            fontSize: '0.875rem',
                            padding: '0 16px',
                            '&:focus': {
                                outline: 'none',
                            },
                            '&:focus-within': {
                                outline: 'none',
                            },
                        },
                        '& .MuiDataGrid-row': {
                            '&:nth-of-type(odd)': {
                                backgroundColor: 'rgba(0, 0, 0, 0.005)',
                            },
                            '&:nth-of-type(even)': {
                                backgroundColor: '#ffffff',
                            },
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.01)',
                            },
                            minHeight: '48px !important',
                            maxHeight: 'none !important'
                        },
                        '& .MuiDataGrid-footerContainer': {
                            backgroundColor: '#ffffff',
                            borderTop: '1px solid rgba(224, 224, 224, 0.4)',
                            borderBottomLeftRadius: '8px',
                            borderBottomRightRadius: '8px'
                        },
                        '& .MuiToolbar-root': {
                            color: 'rgba(0, 0, 0, 0.6)'
                        },
                        '& .MuiTablePagination-root': {
                            color: 'rgba(0, 0, 0, 0.6)'
                        },
                        '& .MuiDataGrid-toolbarContainer': {
                            backgroundColor: '#ffffff',
                            padding: '8px 16px',
                            borderTopLeftRadius: '8px',
                            borderTopRightRadius: '8px',
                            borderBottom: '1px solid rgba(224, 224, 224, 0.3)'
                        },
                        '& .MuiButton-text': {
                            color: '#1976d2'
                        },
                        '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
                            width: '6px',
                            height: '6px',
                        },
                        '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
                            background: '#f5f5f5',
                            borderRadius: '6px',
                        },
                        '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
                            background: '#e0e0e0',
                            borderRadius: '6px',
                        },
                        '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover': {
                            background: '#bdbdbd',
                        },
                    }
                }
            }
        }
    });

    return (
        <ThemeProvider theme={customTheme}>
            <Box 
                sx={{ 
                    width: '100%', 
                    height: height, 
                    display: 'flex', 
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >
                <Box 
                    sx={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2
                    }}
                >
                    <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                        {filteredRows.length} {filteredRows.length === 1 ? 'record' : 'records'} found
                    </Typography>
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Search in all columns..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'action.active', fontSize: '1.2rem' }} />
                                </InputAdornment>
                            ),
                            sx: {
                                borderRadius: '6px',
                                backgroundColor: 'white',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(0, 0, 0, 0.12)'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(25, 118, 210, 0.5)'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#1976d2'
                                },
                                height: '40px',
                                width: '300px'
                            }
                        }}
                    />
                </Box>
                <Box sx={{ 
                    flexGrow: 1, 
                    width: '100%',
                    overflow: 'hidden',
                    maxHeight: 'calc(100% - 60px)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
                    borderRadius: '8px',
                }}>
                    <DataGrid
                        rows={filteredRows}
                        columns={columnsWithActions || columns}
                        pagination
                        components={{ Toolbar: GridToolbar }}
                        componentsProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            }
                        }}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 25
                                }
                            },
                            sorting: {
                                sortModel: [{ field: 'id', sort: 'asc' }]
                            }
                        }}
                        pageSizeOptions={[10, 25, 50, 100]}
                        getRowClassName={getRowClassName}
                        getRowHeight={() => 'auto'}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        disableDensitySelector={false}
                        disableColumnSelector={false}
                        disableColumnFilter={false}
                        sx={{
                            height: '100%',
                            width: '100%',
                            ...sx
                        }}
                        {...props}
                    />
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default DataTableRCM;
